import React, { FunctionComponent, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Copy } from 'lucide-react';
import { CheckupRequestInfo, Permissions } from '@companion-professional/dctypes';
import {
  Button,
  buttonVariants,
  QueryLoaderWrapper,
  TextLink,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@companion-professional/components';
import { USER_SELECTABLE_LOCATION_TAGS } from '../../lib/config';
import { CheckupLocationSelection } from '../../components/CheckupLocationSelection';
import { useSetCheckupLocationTag } from '../../hooks/useSetCheckupLocationTag';
import { DetailElement } from './DetailElement';
import { Section } from '../../components/Section';
import { CheckupAdvancedInfoDialog } from '../../components/CheckupAdvancedInfoDialog';
import { EventCardsPrinterDialog } from '../../components/EventCardsPrinterDialog';
import { Archive, Printer, PrinterCheck } from 'lucide-react';
import { CheckupPrinterDialog } from '../../components/CheckupPrinterDialog';
import { ArchiveCheckupDialog } from '../../components/ArchiveCheckupDialog';
import { CheckupLinkButtons } from '../../components/CheckupLinkButtons';
import { useHasPermission } from '../../state/auth';
import { usePet } from '../../hooks/usePet';
import { Check } from 'lucide-react';

interface CheckupDetailsProps {
  checkup: CheckupRequestInfo;
  returnUrl: string;
}

// CheckupDetails is the section of the Checkup page that shows the details of the checkup request.
export const CheckupDetails: FunctionComponent<CheckupDetailsProps> = ({ checkup, returnUrl }) => {
  const { isPending, error, setCheckupLocationTag } = useSetCheckupLocationTag();
  const [isCopied, setIsCopied] = React.useState(false);
  const { pet } = usePet(checkup.pet_id);
  const canArchiveCheckup = useMemo(() => {
    if (!checkup) {
      return false;
    }

    return !checkup.answered_surveys?.length && checkup.num_asset_ids === 0;
  }, [checkup]);

  // enableLabelPrintButtons turns on the print buttons only for users who can print labels.  We may want to turn this
  // off entirely because this feature is really only useful for Companion events and requires that you have the DYMO
  // printer.
  const enableLabelPrintButtons = useHasPermission(Permissions.checkup_label_print);

  return (
    <Section className="py-4">
      <div className="w-full flex-1 p-4">
        <div className="grid flex-1 grid-cols-[25%_75%] gap-2 lg:grid-cols-[15%_35%_15%_35%]">
          <DetailElement label="Requested By">
            {`${checkup.vet_first_name} ${checkup.vet_last_name}`.trim()}
          </DetailElement>
          <DetailElement label="Date Created">
            {format(parseISO(checkup.request_created), 'MM/dd/yyyy hh:mm a')}
          </DetailElement>
          <DetailElement label="Pet Id">
            <div className="flex items-center gap-2">
              {pet?.integration_url?.url && pet?.integration_url?.is_clickable ? (
                <TextLink
                  href={pet.integration_url.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block max-w-[200px] truncate"
                  title={checkup.clinic_pet_id}
                >
                  {checkup.clinic_pet_id}
                </TextLink>
              ) : (
                <span className="block max-w-[200px] truncate" title={checkup.clinic_pet_id}>
                  {checkup.clinic_pet_id}
                </span>
              )}
              {pet?.integration_url?.url && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      // already verified that integration_url is not undefined, and exists so not checking for null here.
                      navigator.clipboard.writeText(pet.integration_url!.url);
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 500);
                    }}
                  >
                    <Copy size="14" />
                  </Button>
                  {isCopied && (
                    <div className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-primary text-on-primary">
                      <Check className="h-2 w-2" strokeWidth={3} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </DetailElement>
          <DetailElement label="Pet Name">
            <TextLink asChild>
              <Link to={`/pets/${checkup.pet_id}`}>
                <span>{checkup.pet_name}</span>
              </Link>
            </TextLink>
          </DetailElement>
          <DetailElement label="Location">
            <QueryLoaderWrapper isPending={isPending} error={error} size="small">
              <CheckupLocationSelection
                onTagSelect={(tag) => setCheckupLocationTag({ checkupId: checkup.id, petId: checkup.pet_id, tag })}
                locationTags={USER_SELECTABLE_LOCATION_TAGS}
                selectedCheckupTags={checkup.tags}
              />
            </QueryLoaderWrapper>
          </DetailElement>
        </div>
      </div>
      <div className="mt-4 flex flex-row justify-between">
        <div className="flex flex-row justify-start gap-2">
          <CheckupAdvancedInfoDialog checkup={checkup} />
          {enableLabelPrintButtons ? (
            <Tooltip>
              <TooltipTrigger>
                <EventCardsPrinterDialog checkupRequest={checkup}>
                  <div className={buttonVariants({ variant: 'primary', size: 'smallThenIcon' })}>
                    <PrinterCheck size="18" className="lg:mr-2" />
                    <span className="hidden lg:block"> Event Cards</span>
                  </div>
                </EventCardsPrinterDialog>
              </TooltipTrigger>
              <TooltipContent>
                <p>Print Event Cards</p>
                <p>Opens a dialog were you can print the cards used at a Companion event.</p>
              </TooltipContent>
            </Tooltip>
          ) : null}
          {enableLabelPrintButtons ? (
            <Tooltip>
              <TooltipTrigger>
                <CheckupPrinterDialog checkupRequest={checkup}>
                  <div className={buttonVariants({ variant: 'primary', size: 'smallThenIcon' })}>
                    <Printer size="18" className="lg:mr-2" />
                    <span className="hidden lg:block"> Reminder Card</span>
                  </div>
                </CheckupPrinterDialog>
              </TooltipTrigger>
              <TooltipContent>
                <p>Print Checkup Reminder Card</p>
                <p>Opens a dialog were you can print a checkup reminder card with the QR code.</p>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        <div className="flex items-end justify-end gap-2">
          {/* TODO: Add tooltip to help with explaining why the button is disabled.  The current tooltip component
                   doesn't visible work well with the Dialog trigger and the Button component.  We should update the
                   current tooltip component or look into creating or using a better option. */}
          <ArchiveCheckupDialog checkupRequest={checkup} returnUrl={returnUrl}>
            <Button size="smallThenIcon" variant="destructiveOutline" type="button" disabled={!canArchiveCheckup}>
              <Archive size="18" className="lg:mr-2" />
              <span className="hidden lg:block"> Archive Checkup</span>
            </Button>
          </ArchiveCheckupDialog>
          <CheckupLinkButtons checkupRequest={checkup} />
        </div>
      </div>
    </Section>
  );
};
